import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth } from '@/context/Auth';
import { subscribeToNotifications } from '@/services/realtime';
import {
  dataRefreshRequested,
  fetchNotifications,
  notificationReceived,
  notificationRemoved,
  notificationUpdated,
  resetNotificationsState,
  setRealtimeConnection,
} from '@/redux/notificationsSlice';
import { fetchAgents } from '@/redux/agentsSlice';
import { fetchConversations } from '@/redux/conversationsSlice';
import type { AppDispatch } from '@/redux/store';

/**
 * A finished call writes its notification before the summary and enhanced
 * transcript exist — those are produced asynchronously afterwards. Waiting a
 * beat before refetching means the conversation usually lands in the tables
 * complete, and it collapses a burst of notifications into a single refetch.
 */
const DATA_REFRESH_DEBOUNCE_MS = 1_500;

/**
 * Owns the notification channel for the signed-in user.
 *
 * Two jobs:
 *  1. Keep the bell fed (live inserts, plus catch-up after any gap).
 *  2. Keep the rest of the app fresh — a realtime event schedules a debounced
 *     refetch of agents and conversations, which is what makes new calls and
 *     contacts appear in the tables without a reload.
 *
 * Mount once, high in the tree. Mounting it twice would open two channels.
 */
export function useRealtimeNotifications() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const userId = user?.id;

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!userId) {
      dispatch(resetNotificationsState());
      return;
    }

    let cancelled = false;

    const refreshAppData = () => {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        // Conversation data lives in two slices: nested inside each agent (from
        // the get_agents RPC) and flat in the conversations slice. Both feed
        // different screens, so both have to be refreshed.
        void dispatch(fetchAgents());
        void dispatch(fetchConversations());
        // Bookings are not in the store — the calendar fetches them itself off
        // a refresh token, so refetching agents does nothing for an
        // appointment the agent booked or moved during the call.
        dispatch(dataRefreshRequested());
      }, DATA_REFRESH_DEBOUNCE_MS);
    };

    void dispatch(fetchNotifications());

    const unsubscribe = subscribeToNotifications(userId, {
      onInsert: notification => {
        if (cancelled) return;
        dispatch(notificationReceived(notification));
        refreshAppData();
      },
      onUpdate: notification => {
        if (cancelled) return;
        dispatch(notificationUpdated(notification));
      },
      onDelete: notificationId => {
        if (cancelled) return;
        dispatch(notificationRemoved(notificationId));
      },
      onConnectionChange: state => {
        if (cancelled) return;
        dispatch(setRealtimeConnection(state));
      },
      onResync: () => {
        if (cancelled) return;
        // The socket was down; anything written meanwhile was never delivered.
        void dispatch(fetchNotifications());
        refreshAppData();
      },
    });

    // A backgrounded tab can have its socket dropped without a channel error
    // ever firing, so returning to the tab is its own catch-up point.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible' || cancelled) return;
      void dispatch(fetchNotifications());
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      cancelled = true;
      clearTimeout(refreshTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      unsubscribe();
    };
  }, [dispatch, userId]);
}
